import { useEffect } from 'react';
import { Link } from 'react-router-dom';

type Lang = 'sk' | 'en';

const copy = {
  sk: {
    title: 'Ako sa k nám dostanete – závod Lozorno | Artifex Systems Slovakia',
    description:
      'Orientačná mapa a pokyny pre návštevy závodu Artifex Systems Slovakia v Lozorne. Registrácia na recepcii v Hale D, vchody D1 a D4.',
    h1: 'Vitajte v Artifex Systems Slovakia. Ako sa k nám dostanete – závod Lozorno.',
    langLabel: 'Jazyk',
    pages: [
      {
        src: '/guides/navstevy-1.svg',
        alt: 'Mapa západného Slovenska s trasou do Automobilového priemyselného parku Lozorno a detailom výjazdu 41 Zohor. Nadpis: Vitajte v Artifex Systems Slovakia.',
      },
      {
        src: '/guides/navstevy-2.svg',
        alt: 'Pokyny pre návštevy. Registrácia v Hale D cez vchod D1 alebo D4. Trasy autom z Bratislavy, Brna a Malaciek, Viedne, Trnavy a Žiliny a po ceste I/2. Lietadlom z letiska Bratislava a z letiska Viedeň-Schwechat.',
      },
      {
        src: '/guides/navstevy-3.svg',
        alt: 'Mapa areálu: diaľnica D2, výjazd 41, cesta I/2, nadjazd, vrátnica, vonkajšie parkovisko pre návštevy, Hala B vchod B, Hala D recepcia D1 a D4, parkovanie pri D1 a zastávka autobusu. V areáli platí jednosmerná premávka a maximálna rýchlosť 20 km/h.',
      },
      {
        src: '/guides/navstevy-4.svg',
        alt: 'Artifex Systems Slovakia s.r.o., člen skupiny Tata AutoComp. Automobilový priemyselný park, súp. č. 1006, 900 55 Lozorno, Slovensko. www.artifex-systems.sk',
      },
    ],
  },
  en: {
    title: 'Directions to the Lozorno plant | Artifex Systems Slovakia',
    description:
      'Orientation map and directions for visitors to the Artifex Systems Slovakia plant in Lozorno. Registration at the Hall D reception, entrances D1 and D4.',
    h1: 'Welcome to Artifex Systems Slovakia. Directions to the Lozorno plant.',
    langLabel: 'Language',
    pages: [
      {
        src: '/guides/visitors-1.svg',
        alt: 'Map of western Slovakia with the route to the Lozorno Automotive Industrial Park and a detail of exit 41 Zohor. Heading: Welcome to Artifex Systems Slovakia.',
      },
      {
        src: '/guides/visitors-2.svg',
        alt: 'Visitor directions. Registration in Hall D via entrance D1 or D4. Routes by car from Bratislava, Brno and Malacky, Vienna, Trnava and Žilina, and via road I/2. By air from Bratislava Airport and Vienna Airport Schwechat.',
      },
      {
        src: '/guides/visitors-3.svg',
        alt: 'Site map: motorway D2, exit 41, road I/2, overpass, gatehouse, outer visitors’ car park, Hall B entrance B, Hall D reception D1 and D4, parking at D1, and the bus stop. A one-way system applies and the speed limit is 20 km/h.',
      },
      {
        src: '/guides/visitors-4.svg',
        alt: 'Artifex Systems Slovakia s.r.o., a Tata AutoComp Group company. Automobilový priemyselný park, No. 1006, 900 55 Lozorno, Slovakia. www.artifex-systems.sk',
      },
    ],
  },
} as const;

const VisitorsGuide = ({ lang }: { lang: Lang }) => {
  const t = copy[lang];

  useEffect(() => {
    const previousTitle = document.title;
    const previousLang = document.documentElement.lang;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute('content') ?? '';

    document.title = t.title;
    document.documentElement.lang = lang;
    description?.setAttribute('content', t.description);

    return () => {
      document.title = previousTitle;
      document.documentElement.lang = previousLang;
      description?.setAttribute('content', previousDescription);
    };
  }, [lang, t.title, t.description]);

  return (
    <div className="visitors-guide min-h-screen bg-[#e7edf2] text-primary">
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 0; }
          .visitors-guide { background: white !important; }
        }
      `}</style>
      <header className="sticky top-0 z-50 border-b border-[hsl(var(--hairline))] bg-background/95 backdrop-blur-md print:hidden">
        <div className="container-tight flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center" aria-label="Artifex — home">
            <img src="/artifex-logo.svg" alt="Artifex Systems Slovakia" className="h-7 w-auto" />
          </Link>
          <nav className="flex items-center rounded-md border border-[hsl(var(--hairline))] p-0.5" aria-label={t.langLabel}>
            <Link
              to="/navstevy"
              aria-current={lang === 'sk' ? 'page' : undefined}
              className={`px-3 py-1.5 font-display text-xs font-semibold tracking-[0.16em] ${
                lang === 'sk' ? 'bg-primary text-primary-foreground' : 'text-primary hover:bg-muted'
              }`}
            >
              SK
            </Link>
            <Link
              to="/visitors"
              aria-current={lang === 'en' ? 'page' : undefined}
              className={`px-3 py-1.5 font-display text-xs font-semibold tracking-[0.16em] ${
                lang === 'en' ? 'bg-primary text-primary-foreground' : 'text-primary hover:bg-muted'
              }`}
            >
              EN
            </Link>
          </nav>
        </div>
      </header>

      <main className="container-tight py-4 md:py-8 print:max-w-none print:p-0">
        <h1 className="sr-only">{t.h1}</h1>
        <div className="flex flex-col gap-4 md:gap-6 print:gap-0">
          {t.pages.map((page, index) => (
            <img
              key={page.src}
              src={page.src}
              alt={page.alt}
              width={842}
              height={595}
              loading={index === 0 ? 'eager' : 'lazy'}
              className="h-auto w-full bg-white shadow-[0_10px_30px_-18px_rgba(33,57,79,0.55)] print:break-after-page print:shadow-none"
            />
          ))}
        </div>
      </main>
    </div>
  );
};

export default VisitorsGuide;
